import React, { useEffect, useRef } from "react";
import { DailyIcon } from "@/features/daily/components/DailyIcon";
import type { EventVm } from "@/features/daily/components/command/health-view-model";

type Props = {
    events: EventVm[] | null;
    failed: boolean;
    onClose: () => void;
    restoreFocusTo: HTMLElement | null;
};

export const EventsDrawer: React.FC<Props> = ({ events, failed, onClose, restoreFocusTo }) => {
    const closeRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        const onKey = (event: KeyboardEvent): void => {
            if (event.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    useEffect(() => {
        if (closeRef.current !== null) closeRef.current.focus();
        return () => {
            if (restoreFocusTo !== null) restoreFocusTo.focus();
        };
    }, [restoreFocusTo]);

    return (
        <div className="daily-cc-drawer">
            <div className="daily-cc-drawer__scrim" onClick={onClose} aria-hidden="true" />
            <aside className="daily-cc-drawer__panel" role="dialog" aria-modal="true" aria-label="Últimos eventos">
                <div className="daily-cc-drawer__hd">
                    <div>
                        <h2 className="daily-cc-drawer__title">Últimos eventos</h2>
                        <span className="daily-small daily-muted">Últimas 24 h</span>
                    </div>
                    <button ref={closeRef} type="button" className="daily-cc-drawer__close" aria-label="Cerrar" onClick={onClose}>
                        <DailyIcon name="close" />
                    </button>
                </div>
                <div className="daily-cc-drawer__body">
                    {failed ? (
                        <p className="daily-small daily-muted" role="status">No se pudieron cargar los eventos. Se reintentará en la próxima actualización.</p>
                    ) : events === null ? (
                        <p className="daily-small daily-muted" role="status">Cargando eventos…</p>
                    ) : events.length === 0 ? (
                        <p className="daily-small daily-muted">No se observó ninguna transición en las últimas 24 horas.</p>
                    ) : (
                        <ol className="daily-event-list" aria-label="Últimos eventos, más reciente primero">
                            {events.map((event) => (
                                <li key={event.key} className="daily-event">
                                    <time dateTime={new Date(event.at).toISOString()}>{event.timeLabel}</time>
                                    <div>
                                        <span className="daily-event__title" data-tone={event.tone}>{event.title}</span>
                                        <small>{event.detail}</small>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    )}
                </div>
            </aside>
        </div>
    );
};
